import { Box, Collapse, Typography } from '@mui/material';
import { FC, useState } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ChevronDownIcon } from 'src/assets/svg/chevron-down.svg';
import { formatAmount } from 'src/utils/format-amount';
import { DiscountBanner } from 'src/components/discount-banner';
import { CustomTooltip } from 'src/components/tooltip';

import { totalPaymentData } from '../booking-summary/constants';

import { getRowStyles } from './payment-plan';
import { styles } from './styles';

import type { PaymentPlanMobileOverviewProps } from './types';

export const PaymentPlanMobileOverview: FC<PaymentPlanMobileOverviewProps> = ({
    showTotalPayment = false,
    hasDiscount,
    currency,
    createdPaymentPlan,
}) => {
    const { t } = useTranslation();
    const [showDetailedPayment, setShowDetailedPayment] = useState<boolean>(false);

    const paymentPlanData = createdPaymentPlan;

    if (!paymentPlanData) return null;

    const toggleDetailedPayment = () => setShowDetailedPayment((prev) => !prev);

    return (
        <Box css={styles.mobilePaymentPlanWrapper}>
            {hasDiscount && (
                <DiscountBanner>
                    <Typography variant="body3">{t('discount_applied_info')}</Typography>
                </DiscountBanner>
            )}
            <Box css={css(styles.mobilePaymentPlan)}>
                {paymentPlanData.promotion && (
                    <Box css={css(styles.mobilePaymentPlanInfoRow, styles.tableRowDiscount)}>
                        {t('payment_plan_discount', {
                            count: paymentPlanData.promotion.number_of_weeks,
                        })}
                        <Typography variant="body3">
                            - {formatAmount(paymentPlanData.promotion.amount.toFixed(2), currency)}
                        </Typography>
                    </Box>
                )}
                <Box css={styles.mobilePaymentPlanInfoRow}>
                    <Typography variant="body3">{t('payment_plan_total_payable')}</Typography>
                    <Typography variant="body3">
                        {formatAmount(paymentPlanData.totalRent.toFixed(2), currency)}
                    </Typography>
                </Box>
                {!showTotalPayment && (
                    <Box css={styles.mobilePaymentPlanInfoRow}>
                        <Typography variant="body3">
                            {t('payment_plan_total_payable_today')}
                        </Typography>
                        <Typography variant="body3">
                            {formatAmount(
                                paymentPlanData.paymentOptions[0].price.toFixed(2),
                                currency
                            )}
                        </Typography>
                    </Box>
                )}
                <Box>
                    <Box css={styles.mobilePaymentPlanInfoRow} onClick={toggleDetailedPayment}>
                        <Typography variant="body3" css={styles.fullPLan}>
                            {t('payment_plan_full')}
                        </Typography>
                        <ChevronDownIcon
                            css={showDetailedPayment && styles.detailedPaymentExpanded}
                        />
                    </Box>

                    <Collapse in={showDetailedPayment}>
                        <Box css={styles.collapseWrapper}>
                            <Box css={css(styles.tableRow, styles.tableRowWithBorder)}>
                                <Box css={styles.firstCell}>
                                    <Typography
                                        variant="h4"
                                        css={styles.mobilePaymentPlanColumnTitle}
                                    >
                                        {t('payment_plan_payment_th')}
                                    </Typography>
                                </Box>
                                <Box css={styles.secondCell}>
                                    <Typography
                                        variant="h4"
                                        css={styles.mobilePaymentPlanColumnTitle}
                                    >
                                        {t('payment_plan_due_date_th')}
                                    </Typography>
                                </Box>
                                <Box css={styles.thirdCell}>
                                    <Typography
                                        variant="h4"
                                        css={styles.mobilePaymentPlanColumnTitle}
                                    >
                                        {t('payment_plan_amount_th')}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box css={css(styles.tableRowWithBorder)}>
                                {paymentPlanData.paymentOptions.map((plan) => (
                                    <Box key={plan.name} css={getRowStyles(false, false)}>
                                        <Box css={styles.firstCell}>
                                            <Typography variant="body4">{plan.name}</Typography>
                                        </Box>
                                        <Box css={styles.secondCell}>
                                            {plan.dueDate ? (
                                                <Typography variant="body4">
                                                    {plan.dueDate}
                                                </Typography>
                                            ) : null}
                                        </Box>
                                        <Box css={styles.thirdCell}>
                                            <Typography variant="body4">
                                                {formatAmount(plan.price.toFixed(2), currency)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Collapse>
                </Box>
                {showTotalPayment && (
                    <Box css={css(styles.tableRow, styles.tableRowTotal)}>
                        <Box css={[styles.firstCell, styles.twoRows]}>
                            <Typography variant="h3" css={styles.totalPay}>
                                {t(totalPaymentData.paymentLocale)}
                            </Typography>
                            <CustomTooltip
                                title={
                                    <Typography variant="body4">
                                        {t('payment_plan_total_payable_info')}
                                    </Typography>
                                }
                            />
                        </Box>
                        <Box css={styles.thirdCell}>
                            <Typography variant="h3" css={styles.totalPay}>
                                {formatAmount(
                                    paymentPlanData.paymentOptions[0].price.toFixed(2),
                                    currency
                                )}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
};
